import { StorefrontLinkCreateRequestType } from "@sellar-io/api-v2/types";
import { ReactElement } from "react";
import QRCode from "react-qr-code";
import { twMerge } from "tailwind-merge";
import { useEvents } from "../../../../hooks";
import { useStorefrontLinkCreate } from "../../../../hooks/api/v2/storefrontLink/useStorefrontLinkCreate";
import { useStorefrontLinkList } from "../../../../hooks/api/v2/storefrontLink/useStorefrontLinkList";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { InputContainer } from "@sellar-io/components";
import { Button, Icon, InputText } from "@sellar-io/components";
import { DialogContent, DialogCTA } from "../../../modal/Dialog";
import { useFullPageDialogContext } from "../../../modal/useFullPageDialogContext";
import { SpinnerLegacy } from "../../..";
import { Toast } from "../../../toast/Toast";
import { useToast } from "../../../toast/useToast.shared";
import { SharePricingDialogAlert } from "./SharePricingDialogAlert";
import {
  SharePricingDialogPage,
  SharePricingSource,
} from "./SharePricingDialogTypes";

export function SharePricingDialogStorefrontLink({
  priceTierId,
  connectionId,
  buyerName,
  source,
}: {
  priceTierId?: number | null;
  connectionId?: number | null;
  buyerName?: string;
  source: SharePricingSource;
}): ReactElement {
  const { track } = useEvents();
  const { business } = useSellarCurrentBusiness();
  const { setPage } = useFullPageDialogContext();
  const { toast } = useToast();
  if (!priceTierId && !connectionId)
    setPage(SharePricingDialogPage.PRICE_TIERS);

  if (priceTierId && connectionId)
    throw new Error(
      "Can not create link with both price tier and connection ids",
    );

  if (!business?.Supplier) throw new Error();

  const hasPriceTierOrConnectionId = !!priceTierId || !!connectionId;
  const storefrontLinkListQuery = useStorefrontLinkList(
    {
      query: [
        ...(priceTierId
          ? ([
              {
                type: "PriceTierId",
                value: priceTierId,
                modifier: "EQUALS",
              },
            ] as const)
          : []),
        ...(connectionId
          ? ([
              {
                type: "ConnectionId",
                value: connectionId,
                modifier: "EQUALS",
              },
            ] as const)
          : []),
      ],
    },
    { enabled: !!business.Supplier && hasPriceTierOrConnectionId },
  );

  const storefrontLinkCreateMutation = useStorefrontLinkCreate();

  const storefrontLink = storefrontLinkListQuery.data?.pages[0]?.[0];
  const link = `${import.meta.env.VITE_URL}/suppliers/${
    business.Supplier.urlId
  }/order?storefrontLink=${storefrontLink?.password}`;

  const loading =
    storefrontLinkListQuery.isLoading ||
    storefrontLinkListQuery.isFetching ||
    storefrontLinkCreateMutation.isPending;
  const onSubmit = () => {
    let storefrontLinkCreateRequest: StorefrontLinkCreateRequestType | null =
      null;

    if (connectionId) {
      storefrontLinkCreateRequest = {
        type: "connection",
        PriceTierId: null,
        SupplierRetailerConnectionId: connectionId,
      };
    }

    if (priceTierId) {
      storefrontLinkCreateRequest = {
        type: "priceTier",
        PriceTierId: priceTierId,
        SupplierRetailerConnectionId: null,
      };
    }
    if (!storefrontLinkCreateRequest)
      throw new Error("must provided either a priceTier id or a connection Id");

    storefrontLinkCreateMutation.mutate(storefrontLinkCreateRequest, {
      onSuccess: () => {
        track("Price Tier Link Activated", {
          supplierName: business.name,
          source,
        });
      },
    });
  };
  return (
    <DialogContent className={"flex h-full flex-col"}>
      {loading ? (
        <SpinnerLegacy />
      ) : !storefrontLink ? (
        <div className={"flex h-full flex-col gap-4"}>
          <SharePricingDialogAlert buyerName={buyerName} />
          <Button onClick={onSubmit} className={"w-full"}>
            <Icon icon={"LINK"} />
            Activate Link
          </Button>
        </div>
      ) : (
        <>
          {!link ? (
            <SpinnerLegacy />
          ) : (
            <div>
              <div className={"flex items-center justify-center"}>
                <QRCode
                  size={256}
                  className={
                    "mb-4 h-40 w-40 rounded-lg border border-solid p-3"
                  }
                  value={link}
                  viewBox={`0 0 256 256`}
                />
              </div>
              <div className={"flex h-full flex-col gap-4"}>
                <InputContainer>
                  <InputText disabled={true} value={link} />
                </InputContainer>
                <div className="flex flex-col gap-2 md:flex-row">
                  <Button
                    className={"w-full"}
                    color={"secondary"}
                    onClick={() => window.open(link, "_blank")}
                  >
                    <Icon icon={"OPEN_IN_NEW"} />
                    Preview link
                  </Button>
                  <Button
                    className={"w-full"}
                    onClick={() => {
                      track("Price Tier Link Copied", {
                        supplierName: business.name,
                        source,
                      });
                      toast({
                        placement: "bottom",
                        render: () => <Toast icon={"LINK"}>Link copied!</Toast>,
                      });
                      navigator.clipboard.writeText(link);
                    }}
                  >
                    <Icon icon={"CONTENT_COPY"} />
                    Copy Link
                  </Button>
                </div>
                <SharePricingDialogAlert buyerName={buyerName} />
              </div>
            </div>
          )}
        </>
      )}
      <DialogCTA
        className={twMerge(
          "drop-shadow-none",
          storefrontLink &&
            !storefrontLinkListQuery.isFetching &&
            "flex flex-row justify-between",
        )}
      >
        {storefrontLink && !storefrontLinkListQuery.isFetching && (
          <Button
            color={"inverse-red"}
            onClick={() =>
              setPage(SharePricingDialogPage.PRICE_TIER_LINK_DEACTIVATE)
            }
          >
            <Icon icon={"LINK_OFF"} />
            Deactivate Link
          </Button>
        )}
        <Button
          className={"text-blue-800"}
          color={"tertiary"}
          onClick={() =>
            window.open(
              "https://sellar.notion.site/Share-your-pricing-and-availability-5cbf77c67c094aebae4722a999417abf",
              "_blank",
            )
          }
        >
          <Icon icon={"HELP_CIRCLE_OUTLINE"} />
          Learn more
        </Button>
      </DialogCTA>
    </DialogContent>
  );
}
